export class WaveHUD {
  private container: HTMLElement;
  private waveDisplay: HTMLElement;
  private enemyCounter: HTMLElement;
  private scoreDisplay: HTMLElement;
  private highScoreDisplay: HTMLElement;
  private messageOverlay: HTMLElement;
  private messageTitle: HTMLElement;
  private messageSubtitle: HTMLElement;
  private gameOverScreen: HTMLElement;

  constructor() {
    this.container = this.createContainer();
    this.waveDisplay = this.createWaveDisplay();
    this.enemyCounter = this.createEnemyCounter();
    this.scoreDisplay = this.createScoreDisplay();
    this.highScoreDisplay = this.createHighScoreDisplay();
    [this.messageOverlay, this.messageTitle, this.messageSubtitle] = this.createMessageOverlay();
    this.gameOverScreen = this.createGameOverScreen();

    this.container.appendChild(this.waveDisplay);
    this.container.appendChild(this.enemyCounter);
    this.container.appendChild(this.scoreDisplay);
    this.container.appendChild(this.highScoreDisplay);

    document.getElementById('ui-container')?.appendChild(this.container);
    document.getElementById('ui-container')?.appendChild(this.messageOverlay);
    document.getElementById('ui-container')?.appendChild(this.gameOverScreen);

    this.hide();
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'wave-hud';
    container.style.cssText = `
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      font-family: 'Courier New', monospace;
      z-index: 100;
    `;
    return container;
  }

  private createWaveDisplay(): HTMLElement {
    const display = document.createElement('div');
    display.style.cssText = `
      color: #00ffff;
      font-size: 28px;
      font-weight: bold;
      text-shadow: 0 0 10px #00ffff, 0 0 20px #00ffff;
      letter-spacing: 3px;
    `;
    display.textContent = 'WAVE 1';
    return display;
  }

  private createEnemyCounter(): HTMLElement {
    const counter = document.createElement('div');
    counter.style.cssText = `
      color: #ff4444;
      font-size: 18px;
      text-shadow: 0 0 8px #ff4444;
      letter-spacing: 2px;
    `;
    counter.textContent = 'ENEMIES: 0';
    return counter;
  }

  private createScoreDisplay(): HTMLElement {
    const display = document.createElement('div');
    display.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      color: #ffff00;
      font-size: 24px;
      font-weight: bold;
      text-shadow: 0 0 10px #ffff00;
      letter-spacing: 2px;
      font-family: 'Courier New', monospace;
    `;
    display.textContent = 'SCORE: 0';
    document.getElementById('ui-container')?.appendChild(display);
    return display;
  }

  private createHighScoreDisplay(): HTMLElement {
    const display = document.createElement('div');
    display.style.cssText = `
      position: absolute;
      top: 50px;
      right: 20px;
      color: #888888;
      font-size: 14px;
      text-shadow: 0 0 5px #888888;
      letter-spacing: 1px;
      font-family: 'Courier New', monospace;
    `;
    display.textContent = 'HIGH: 0';
    document.getElementById('ui-container')?.appendChild(display);
    return display;
  }

  private createMessageOverlay(): [HTMLElement, HTMLElement, HTMLElement] {
    const overlay = document.createElement('div');
    overlay.id = 'wave-message';
    overlay.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      z-index: 150;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    `;

    const title = document.createElement('div');
    title.style.cssText = `
      color: #00ffff;
      font-size: 64px;
      font-weight: bold;
      text-shadow: 0 0 20px #00ffff, 0 0 40px #00ffff;
      letter-spacing: 8px;
      font-family: 'Courier New', monospace;
      animation: pulse 0.5s ease infinite alternate;
    `;

    const subtitle = document.createElement('div');
    subtitle.style.cssText = `
      color: #ffffff;
      font-size: 24px;
      text-shadow: 0 0 10px #ffffff;
      letter-spacing: 3px;
      font-family: 'Courier New', monospace;
    `;

    overlay.appendChild(title);
    overlay.appendChild(subtitle);

    // Add pulse animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        from { transform: scale(1); }
        to { transform: scale(1.05); }
      }
      @keyframes slideIn {
        from { transform: translateY(-50px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    return [overlay, title, subtitle];
  }

  private createGameOverScreen(): HTMLElement {
    const screen = document.createElement('div');
    screen.id = 'wave-gameover';
    screen.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 20px;
      z-index: 200;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.5s ease;
      font-family: 'Courier New', monospace;
    `;

    screen.innerHTML = `
      <div style="color: #ff0000; font-size: 72px; font-weight: bold; text-shadow: 0 0 30px #ff0000; letter-spacing: 10px;">
        GAME OVER
      </div>
      <div id="go-wave" style="color: #00ffff; font-size: 32px; text-shadow: 0 0 15px #00ffff; margin-top: 20px;">
        WAVE: 1
      </div>
      <div id="go-score" style="color: #ffff00; font-size: 48px; font-weight: bold; text-shadow: 0 0 20px #ffff00;">
        SCORE: 0
      </div>
      <div id="go-highscore" style="color: #ff00ff; font-size: 24px; text-shadow: 0 0 10px #ff00ff;">
        HIGH SCORE: 0
      </div>
      <div id="go-newrecord" style="color: #00ff00; font-size: 28px; text-shadow: 0 0 15px #00ff00; display: none;">
        NEW RECORD!
      </div>
      <div style="color: #888888; font-size: 18px; margin-top: 40px; letter-spacing: 2px;">
        PRESS SPACE TO RESTART
      </div>
    `;

    return screen;
  }

  public show(): void {
    this.container.style.display = 'flex';
    this.scoreDisplay.style.display = 'block';
    this.highScoreDisplay.style.display = 'block';
  }

  public hide(): void {
    this.container.style.display = 'none';
    this.scoreDisplay.style.display = 'none';
    this.highScoreDisplay.style.display = 'none';
    this.hideMessage();
    this.hideGameOver();
  }

  public updateWave(wave: number, enemyCount: number): void {
    this.waveDisplay.textContent = `WAVE ${wave}`;
    this.enemyCounter.textContent = `ENEMIES: ${enemyCount}`;
  }

  public updateScore(score: number): void {
    this.scoreDisplay.textContent = `SCORE: ${score.toLocaleString()}`;
  }

  public updateHighScore(highScore: number): void {
    this.highScoreDisplay.textContent = `HIGH: ${highScore.toLocaleString()}`;
  }

  public showMessage(title: string, subtitle: string): void {
    this.messageTitle.textContent = title;
    this.messageSubtitle.textContent = subtitle;
    this.messageOverlay.style.opacity = '1';
  }

  public hideMessage(): void {
    this.messageOverlay.style.opacity = '0';
  }

  public showGameOver(score: number, highScore: number, wave: number): void {
    const waveEl = this.gameOverScreen.querySelector('#go-wave') as HTMLElement;
    const scoreEl = this.gameOverScreen.querySelector('#go-score') as HTMLElement;
    const highScoreEl = this.gameOverScreen.querySelector('#go-highscore') as HTMLElement;
    const newRecordEl = this.gameOverScreen.querySelector('#go-newrecord') as HTMLElement;

    waveEl.textContent = `WAVE: ${wave}`;
    scoreEl.textContent = `SCORE: ${score.toLocaleString()}`;
    highScoreEl.textContent = `HIGH SCORE: ${highScore.toLocaleString()}`;

    if (score >= highScore && score > 0) {
      newRecordEl.style.display = 'block';
    } else {
      newRecordEl.style.display = 'none';
    }

    this.gameOverScreen.style.opacity = '1';
    this.gameOverScreen.style.pointerEvents = 'auto';
  }

  public hideGameOver(): void {
    this.gameOverScreen.style.opacity = '0';
    this.gameOverScreen.style.pointerEvents = 'none';
  }

  public destroy(): void {
    this.container.remove();
    this.scoreDisplay.remove();
    this.highScoreDisplay.remove();
    this.messageOverlay.remove();
    this.gameOverScreen.remove();
  }
}
